<script lang="ts" setup>
import type {
  Configuration,
  SurfaceOptionsGroup,
  SurfaceTypesMapValue,
  SurfaceMaps,
  SurfaceCategoriesMapValue,
  SurfaceCollectionsMapValue,
} from '~/types/configurator'

const chosenCategory = ref(-1)
const chosenCollection = ref(-1)
const filtersOpen = ref(false)

const props = defineProps<{
  activePanel: string | null
  configuration: Configuration | null
  currentValues: Configuration['values']
  chosenParts: Set<[number, number | null | undefined]>
  globalSurfaceChanges: Map<string, Map<number, boolean>>
  surfaceOptionsGroups: Map<string, SurfaceOptionsGroup>
  surfaceTypes: Map<number, SurfaceTypesMapValue>
  surfaceMaps: SurfaceMaps
  surfaceCategoryMismatch: Map<string, Map<number, boolean>>
  activeSurfaceOption: [string, number] | undefined
}>()

watch(
  () => props.activePanel,
  () => {
    filtersOpen.value = false
    chosenCategory.value = -1
    chosenCollection.value = -1
  }
)

const surfaceCategories = computed(() => {
  if (props.activeSurfaceOption?.length !== 2) {
    return new Map<number, SurfaceCategoriesMapValue>()
  }

  return props.surfaceOptionsGroups
    .get(props.activeSurfaceOption[0])
    ?.surfaceOptionGroups.get(props.activeSurfaceOption[1])
    ?.surfaceOptions.at(0)
    ?.surfaceCollectionNids.reduce((acc, collectionNid) => {
      const { category } = configuratorUtils.findSurfaceCollectionByNid(
        props.surfaceTypes,
        props.surfaceMaps,
        collectionNid
      )
      if (category && !acc.get(category.nid)) {
        acc.set(category.nid, category)
      }
      return acc
    }, new Map<number, SurfaceCategoriesMapValue>())
})

const surfaceCollections = computed(() => {
  if (props.activeSurfaceOption?.length !== 2) {
    return new Map<number, SurfaceCollectionsMapValue>()
  }

  if (chosenCategory.value !== -1 && surfaceCategories.value?.size !== 1) {
    return props.surfaceOptionsGroups
      .get(props.activeSurfaceOption[0])
      ?.surfaceOptionGroups.get(props.activeSurfaceOption[1])
      ?.surfaceOptions.at(0)
      ?.surfaceCollectionNids.reduce((acc, collectionNid) => {
        const { category, collection } =
          configuratorUtils.findSurfaceCollectionByNid(
            props.surfaceTypes,
            props.surfaceMaps,
            collectionNid
          )
        if (
          category &&
          collection &&
          category.nid === chosenCategory.value &&
          !acc.get(collection.nid)
        ) {
          acc.set(collection.nid, collection)
        }
        return acc
      }, new Map<number, SurfaceCollectionsMapValue>())
  }

  return props.surfaceOptionsGroups
    .get(props.activeSurfaceOption[0])
    ?.surfaceOptionGroups.get(props.activeSurfaceOption[1])
    ?.surfaceOptions.at(0)
    ?.surfaceCollectionNids.reduce((acc, collectionNid) => {
      const { collection } = configuratorUtils.findSurfaceCollectionByNid(
        props.surfaceTypes,
        props.surfaceMaps,
        collectionNid
      )
      if (collection && !acc.get(collection.nid)) {
        acc.set(collection.nid, collection)
      }
      return acc
    }, new Map<number, SurfaceCollectionsMapValue>())
})

const showFilters = computed(() => {
  const moreThanOneCategory =
    surfaceCategories.value && surfaceCategories.value.size > 1
  const moreThanOneCollection =
    surfaceCollections.value && surfaceCollections.value.size > 1

  return {
    category: moreThanOneCategory,
    collection:
      ((moreThanOneCategory && chosenCategory.value !== -1) ||
        (surfaceCategories.value && surfaceCategories.value.size === 1)) &&
      moreThanOneCollection,
  }
})

const emit = defineEmits<{
  updateSurfaces: [
    option: Configuration['surfaceTypes'][number]['surfaceCategories'][number]['surfaceCollections'][number]['surfaces'][number],
  ]
  setGlobalSurfaceChanges: [
    surfaceOptionsGroupsKey: string,
    surfaceOptionGroupsKey: number,
    globalSurfaceChange: boolean,
    value?: Configuration['values'][number],
  ]
  setChosenParts: [
    surfaceOptionsGroupsKey: string,
    surfaceOptionGroupsKey: number,
    partCategoryNid?: number,
    accessoryNid?: number | null,
  ]
}>()

const userStore = useUser()
const { showPrices } = storeToRefs(userStore)

const selectedSurfaceNid = computed(() => {
  const firstChosenPart = Array.from(props.chosenParts)[0]
  if (!firstChosenPart) {
    return null
  }
  const [partCategoryNid, accessoryNid] = firstChosenPart
  return configuratorUtils.findValue(
    props.currentValues,
    partCategoryNid,
    accessoryNid
  )?.surfaceNid
})

function sumPriceIncrements(
  priceIncrements: Configuration['surfaceTypes'][number]['surfaceCategories'][number]['surfaceCollections'][number]['surfaces'][number]['priceIncrements']
) {
  return priceIncrements.reduce((acc, increment) => {
    const { accessory } = configuratorUtils.findAccessory(
      props.configuration?.accessoriesOptions,
      increment.accessoryCategoryNid,
      increment.accessoryNid
    )
    return acc + (accessory?.price || 0)
  }, 0)
}

const hasImpartOnPrice = computed(() => {
  if (props.activeSurfaceOption?.length !== 2) {
    return false
  }

  const chosenParts = Array.from(props.chosenParts).map(
    ([partCategoryNid, _]) => partCategoryNid
  )
  const surfaceOptions = props.surfaceOptionsGroups
    .get(props.activeSurfaceOption[0])
    ?.surfaceOptionGroups.get(props.activeSurfaceOption[1])
    ?.surfaceOptions.filter((surfaceOption) =>
      chosenParts.includes(surfaceOption.partCategoryNid)
    )
  return surfaceOptions?.some((surfaceOption) => surfaceOption.hasImpactOnPrice)
})

function getCategoryPriceDifference(
  priceDifference: Configuration['surfaceTypes'][number]['surfaceCategories'][number]['priceDifference']
) {
  return hasImpartOnPrice.value ? priceDifference || 0 : 0
}
</script>

<template>
  <div
    v-if="
      activeSurfaceOption?.length === 2 &&
      activePanel ===
        SURFACES_OPTIONS_PANEL + activeSurfaceOption[0] + activeSurfaceOption[1]
    "
    class="flex h-full flex-col gap-1"
  >
    <ProductConfiguratorSurfacesOptionsInner
      v-if="surfaceOptionsGroups.size > 0"
      :current-values="currentValues"
      :configuration="configuration"
      :surface-category-mismatch="surfaceCategoryMismatch"
      :global-surface-changes="globalSurfaceChanges"
      :surface-types="surfaceTypes"
      :surface-maps="surfaceMaps"
      :surface-options-groups="surfaceOptionsGroups"
      :active-surface-option="activeSurfaceOption"
      :chosen-parts="chosenParts"
      @set-chosen-parts="
        (
          surfaceOptionsGroupsKey,
          surfaceOptionGroupsKey,
          partCategoryNid,
          accessoryNid
        ) =>
          emit(
            'setChosenParts',
            surfaceOptionsGroupsKey,
            surfaceOptionGroupsKey,
            partCategoryNid,
            accessoryNid
          )
      "
      @set-global-surface-changes="
        (
          surfaceOptionsGroupsKey,
          surfaceOptionGroupsKey,
          globalSurfaceChange,
          value
        ) => {
          emit(
            'setGlobalSurfaceChanges',
            surfaceOptionsGroupsKey,
            surfaceOptionGroupsKey,
            globalSurfaceChange,
            value
          )
        }
      "
    />
    <div
      v-if="showFilters.category || showFilters.collection"
      class="flex flex-col gap-1"
    >
      <div>
        <button
          class="flex w-full items-center justify-between bg-white p-5 hover:bg-neutral-100 2xl:py-7"
          @click="filtersOpen = !filtersOpen"
        >
          <p class="font-pragmatica-ext font-bold">
            {{
              showFilters.category
                ? $t('configurator.surfaceCategory')
                : $t('configurator.surfaceCollection')
            }}
          </p>
          <div class="flex items-center justify-end gap-4">
            <button
              v-if="chosenCategory !== -1 || chosenCollection !== -1"
              class="rounded px-2 py-0.5 text-sm hover:bg-neutral-200"
              @click.stop="
                () => {
                  chosenCategory = -1
                  chosenCollection = -1
                }
              "
            >
              {{ $t('configurator.removeFilters') }}
            </button>
            <Icon
              :name="
                filtersOpen ? 'fa6-solid:chevron-up' : 'fa6-solid:chevron-down'
              "
            ></Icon>
          </div>
        </button>
        <div v-if="filtersOpen" class="flex flex-col gap-1">
          <div v-if="showFilters.category" class="bg-white p-5">
            <ProductConfiguratorFilterCarousel>
              <UiCarouselItem
                v-for="[nid, category] in surfaceCategories?.entries()"
                :key="nid"
                class="basis-auto last-of-type:pr-3"
              >
                <button
                  class="flex w-fit flex-col items-center whitespace-nowrap rounded-md px-6 py-2"
                  :class="{ 'bg-neutral-200': chosenCategory === nid }"
                  @click="
                    () => {
                      if (chosenCategory === nid) {
                        chosenCategory = -1
                        chosenCollection = -1
                        return
                      }

                      chosenCategory = nid
                      chosenCollection = -1
                    }
                  "
                >
                  <span class="font-bold">
                    {{ $t('configurator.category') }} {{ category.title }}
                  </span>
                  <span v-if="showPrices" class="font-pragmatica-ext text-xs">
                    {{
                      configuratorUtils.getFormattedPrice(
                        getCategoryPriceDifference(category.priceDifference),
                        props.configuration?.currency,
                        true,
                        false
                      )
                    }}
                  </span>
                </button>
              </UiCarouselItem>
            </ProductConfiguratorFilterCarousel>
          </div>
          <div v-if="showFilters.collection" class="bg-white p-5">
            <p
              v-if="showFilters.category"
              class="font-pragmatica-ext mb-4 font-bold"
            >
              {{ $t('configurator.surfaceCollection') }}
            </p>
            <ProductConfiguratorFilterCarousel>
              <UiCarouselItem
                v-for="[nid, collection] in surfaceCollections?.entries()"
                :key="nid"
                class="basis-auto last-of-type:pr-3"
              >
                <button
                  class="flex w-fit flex-col items-center whitespace-nowrap rounded-md px-6 py-2"
                  :class="{
                    'bg-neutral-200': chosenCollection === nid,
                  }"
                  @click="
                    () => {
                      if (chosenCollection === nid) {
                        chosenCollection = -1
                        return
                      }

                      chosenCollection = nid
                    }
                  "
                >
                  <span class="font-bold">
                    {{ collection.title }}
                  </span>
                  <span v-if="showPrices" class="font-pragmatica-ext text-xs">
                    {{
                      configuratorUtils.getFormattedPrice(
                        getCategoryPriceDifference(
                          surfaceCategories?.get(chosenCategory)
                            ?.priceDifference
                        ) + sumPriceIncrements(collection.priceIncrements),
                        props.configuration?.currency,
                        true,
                        false
                      )
                    }}
                  </span>
                </button>
              </UiCarouselItem>
            </ProductConfiguratorFilterCarousel>
          </div>
        </div>
      </div>
    </div>
    <div class="grid flex-grow grid-cols-1 gap-1 overflow-y-auto">
      <template
        v-for="[categoryNid, category] in surfaceCategories?.entries()"
        :key="categoryNid"
      >
        <div
          v-if="chosenCategory === -1 || chosenCategory === categoryNid"
          class="grid grid-cols-1 gap-1"
        >
          <template
            v-for="[collectionNid, collection] in Array.from(
              category.surfaceCollections.entries()
            )
              .filter(([nid, _]) =>
                Array.from(surfaceCollections?.keys() || []).includes(nid)
              )
              .sort(
                (collectionA, collectionB) =>
                  collectionA[1].weight - collectionB[1].weight
              )"
            :key="collectionNid"
          >
            <div
              v-if="
                chosenCollection === -1 || chosenCollection === collectionNid
              "
              class="bg-white"
            >
              <div class="px-5 pb-2 pt-5">
                <div class="flex flex-wrap items-baseline gap-2">
                  <p class="my-5 text-2xl font-bold">
                    {{ collection.title }}
                  </p>
                  <p class="my-5">
                    {{ $t('configurator.category') }}
                    {{ category.title }}
                  </p>
                  <div v-if="showPrices" class="ml-auto">
                    <UiTooltip
                      placement="top"
                      :label="
                        $t('configurator.surfaceCategoryCollectionPriceHelp', {
                          category: category.title,
                          collection: collection.title,
                        })
                      "
                      :offset-size="5"
                      strategy="absolute"
                      class="max-w-s whitespace-normal break-normal rounded-none border-[1px] border-neutral-200 px-3 py-3 text-sm font-normal drop-shadow-sm"
                    >
                      <p class="font-pragmatica-ext font-bold">
                        {{
                          configuratorUtils.getFormattedPrice(
                            getCategoryPriceDifference(
                              category.priceDifference
                            ) + sumPriceIncrements(collection.priceIncrements),
                            props.configuration?.currency,
                            true,
                            false
                          )
                        }}
                      </p>
                    </UiTooltip>
                  </div>
                </div>
                <div
                  class="grid grid-cols-2 gap-x-4 gap-y-2 min-[600px]:grid-cols-3 md:grid-cols-4 lg:grid-cols-5"
                >
                  <button
                    v-for="[
                      surfaceNid,
                      surface,
                    ] in collection.surfaces.entries()"
                    :key="surfaceNid"
                    class="font-pragmatica-ext group relative w-full text-black hover:bg-neutral-200"
                    :class="{
                      'rounded-full bg-neutral-100 px-3 py-1':
                        !surface.image?.url,
                      '!bg-black !text-white':
                        selectedSurfaceNid === surfaceNid,
                    }"
                    @click="emit('updateSurfaces', surface)"
                  >
                    <div class="">
                      <NuxtImg
                        v-if="surface.image?.url"
                        :src="
                          withHost(surface.image?.url + IMG_SIZE_100_100_FIT)
                        "
                        class="aspect-square h-full w-full"
                        alt=""
                      />
                      <div
                        v-if="surface.image?.url"
                        class="absolute bottom-[36px] left-0 right-0 top-0 group-hover:shadow-[inset_0px_0px_0px_4px_rgba(0,0,0,.1)]"
                        :class="{
                          '!shadow-[inset_0px_0px_0px_4px_rgba(0,0,0,1)]':
                            selectedSurfaceNid === surface.nid,
                          '!bottom-[52px]': showPrices,
                        }"
                      ></div>
                    </div>
                    <div class="col-start-1 row-start-1 p-2">
                      <p class="truncate text-sm font-bold uppercase">
                        {{ surface.title }}
                      </p>
                      <p v-if="showPrices" class="text-xs">
                        {{
                          configuratorUtils.getFormattedPrice(
                            getCategoryPriceDifference(
                              category.priceDifference
                            ) +
                              sumPriceIncrements(collection.priceIncrements) +
                              sumPriceIncrements(surface.priceIncrements),
                            props.configuration?.currency,
                            true,
                            false
                          )
                        }}
                      </p>
                    </div>
                  </button>
                </div>
              </div>
              <ProductConfiguratorSurfaceOptionsPanelSurfaceProperties
                :collection="collection"
              />
            </div>
          </template>
        </div>
      </template>
    </div>
  </div>
</template>
